import { Link } from "react-router-dom";
import { ShieldAlert, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CentralClock, greeting } from "@/lib/time";

const TONES = {
  sky: "from-sky-500 to-cyan-400 text-white", emerald: "from-emerald-500 to-teal-400 text-white",
  amber: "from-amber-400 to-orange-500 text-white", rose: "from-rose-500 to-pink-500 text-white",
};

export const ColorStat = ({ icon: Icon, labelText, value, hint, tone, testid }) => (
  <div className={`lift relative overflow-hidden rounded-2xl bg-gradient-to-br p-5 shadow-lg ${TONES[tone]}`} data-testid={testid}>
    <Icon className="absolute -right-3 -top-3 h-14 w-14 opacity-15 sm:h-20 sm:w-20" />
    <p className="relative text-[10px] font-semibold uppercase tracking-[0.12em] opacity-90 sm:text-xs">{labelText}</p>
    <p className="mt-2 font-display text-4xl font-extrabold">{value}</p>
    {hint && <p className="mt-1 text-xs opacity-90">{hint}</p>}
  </div>
);

export const RecruiterHero = ({ title, sub, children }) => {
  const { user } = useAuth();
  return (
    <div className="relative mb-8 overflow-hidden rounded-3xl bg-[linear-gradient(120deg,#0f766e_0%,#0369a1_45%,#1e293b_100%)] p-6 text-white shadow-xl sm:p-8 fade-up" data-testid="recruiter-hero">
      <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-amber-400/30 blur-3xl" />
      <div className="absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-rose-400/25 blur-3xl" />
      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold text-amber-300">{greeting()}, {user.name?.split(" ")[0] || "there"} · {user.org_name || "Your company"}</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="page-title">{title}</h1>
          {sub && <p className="mt-2 max-w-xl text-sm text-sky-100">{sub}</p>}
          {children && <div className="mt-5 flex flex-wrap gap-2">{children}</div>}
        </div>
        <CentralClock className="rounded-2xl border border-white/20 bg-white/10 px-5 py-3 backdrop-blur" />
      </div>
    </div>
  );
};

export const WorkEmailBanner = () => {
  const { user } = useAuth();
  if (user.role !== "recruiter" || (user.email_verified && !user.personal_email)) return null;
  const personal = user.personal_email;
  return (
    <Link to="/recruiter/verify" className={`mb-6 flex items-center gap-3 rounded-2xl border p-4 text-sm font-medium ${personal ? "border-rose-200 bg-rose-50 text-rose-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="work-email-banner">
      {personal ? <ShieldAlert className="h-5 w-5 shrink-0" /> : <MailCheck className="h-5 w-5 shrink-0" />}
      {personal ? `For security, hiring accounts must use a company email. Switch from ${user.email} to keep contacting candidates and posting jobs →` : `Enter the 6-digit code we emailed to ${user.email} to activate your account →`}
    </Link>
  );
};
